// A development's Sheet tab (PropertyDetail, /app/properties/<id>?tab=sheet) — the reservation sheet for a scheme coming
// to market (Task G; prototype §16 agent portal, §17/§18 reservation sheet). Since 26 Oct 2026 (later; user: "sheets are
// just to create flats in bulk by importing sheets, that's it") this is the only place a sheet shows: Lettings →
// Applications is one table of every application and Lettings → Available the free advertised flats (ApplicationsTab.tsx,
// AvailableTab.tsx; docs/plans/lettings-one-form.md "Lettings without sheets"). What stays here: set up a sheet, "Edit the
// flat list" / "Paste from a spreadsheet", the agents on it (their colours), open / close, Export CSV, and the sheet itself
// (tiles, Table / Cards) — a booked flat opens the ApplicationDrawer, a free one the ReferDrawer (Register for home, which
// needs the flat's published advert).
//
// Only that development's sheets are listed (chips when there are several, e.g. a closed one and a new one), "Set up a
// sheet" is fixed to it (the empty state says how many flats would go on), the sheet's actions go to the page's ⋯ menu
// through `onMenu`, and "New sheet" shows only once the sheet is closed (one open sheet per development, as in the demo).
// ?sheet=<id> picks one of its sheets; ?flat=<id> opens a flat. The tab shows only to a role holding reservations.read;
// the API applies the same scope either way.
//
// Everything staff do to the sheet is in the ⋯ menu: Start the sheet / Close the sheet (a new sheet always starts open to agents),
// Edit the flat list, Export CSV, New sheet (once closed). "Edit the flat list" (prototype §18 resFlatEditor): the flats on
// the sheet (a free one can be taken off), the development's other free flats to add, and "Paste from a spreadsheet" —
// Floor, Flat no., Type, Size, Rent out of Excel, parsed here into rows for POST /reservations/sheets/:id/flats (a new
// reference becomes a unit). Stage and agent colours keep their defaults (PATCH /reservations/sheets/:id still takes them).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStatFilter } from '../../hooks/useStatFilter';
import { FilterMenu } from '../ui/FilterMenu';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, Trash2 } from 'lucide-react';
import { Segmented, Button, Modal, Input, Select, Textarea, Field, EmptyState, type MenuEntry, SkeletonRows } from '../ui';
import { FormError, Confirm } from '../forms/common';
import { useCan } from '../../hooks/usePermission';
import { api } from '../../lib/api';
import { formatMoney } from '../../lib/viz';
import {
  reservationsApi, floorLabel, bedsLabel,
  type Flat, type SheetData, type SheetSummary, type PastedFlat, type AddFlatsResult,
} from '../../lib/reservations';
import { PageLoader } from '../BrandLoader';
import { ApplicationDrawer } from './ApplicationDrawer';
import { ReferDrawer } from './ReferDrawer';
import { PhaseChip, StageChip, RoadmapChip, RoadmapDots } from './applicationParts';

/** Who the flat is with: the agent, or "Direct (app)" when the tenant booked it themselves in the app (no agent). */
const DIRECT_COLOUR = '#8E7150';
const agentLabel = (f: Flat) => f.agent?.name ?? (f.direct ? 'Direct (app)' : null);
const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');

export function ReservationsTab({ propertyId, propertyName, onMenu }: {
  /** The development (the development page's Sheet tab — the only place a sheet shows since 2026-10-26). */
  propertyId: string;
  propertyName?: string;
  /** Hand the sheet's actions up to the page's ⋯ menu. */
  onMenu?: (m: MenuEntry[]) => void;
}) {
  const [params, setParams] = useSearchParams();
  const canCreate = useCan('reservations.create');
  const canInvoice = useCan('agentBills.create');
  const [creating, setCreating] = useState(false);

  const sheets = useQuery({ queryKey: ['/reservations/sheets', { propertyId }], queryFn: () => reservationsApi.sheets({ propertyId }) });
  const list = sheets.data ?? [];
  // ?sheet= is honoured only when it names one of the sheets listed (a stale id from another sheet is ignored).
  const wanted = params.get('sheet');
  const sheetId = (wanted && list.some((s) => s.id === wanted) ? wanted : null) ?? list.find((s) => s.status === 'LIVE')?.id ?? list[0]?.id ?? '';
  const pick = (id: string) => { const n = new URLSearchParams(params); n.set('sheet', id); n.delete('flat'); n.delete('propertyId'); setParams(n, { replace: true }); };

  // On a development page with no sheet yet: how many flats would go on one (the empty state's line, and whether "Set up" can work).
  const noSheet = !sheets.isLoading && !list.length;
  const eligible = useQuery({ queryKey: ['/reservations/eligible', propertyId], queryFn: () => reservationsApi.eligible(propertyId), enabled: noSheet });
  const freeCount = eligible.data?.flats.length ?? null;
  const canSetUp = canCreate && (freeCount === null || freeCount > 0);
  // "Set up a sheet" sits in the page's ⋯ menu as well as on the empty state.
  useEffect(() => {
    if (!onMenu || !noSheet) return;
    onMenu([canSetUp && { label: 'Set up a sheet', icon: 'Plus', onClick: () => setCreating(true) }]);
    return () => onMenu([]);
  }, [onMenu, noSheet, canSetUp]);

  if (sheets.isLoading) return <PageLoader />;
  if (!list.length) {
    const e = eligible.data;
    const line = !e ? '' : e.flats.length
      ? `${e.flats.length} of ${e.total} flats will go on the sheet — the ones not let and with no tenancy lined up.`
      : `All ${e.total} flats are let or have a tenancy lined up, so there is nothing to put on a sheet yet.`;
    return (
      <div>
        <EmptyState icon="ClipboardList" title="No reservation sheet for this development" body={`A reservation sheet puts a building's flats in at once — from the development's units or pasted from a spreadsheet — and shows the agents on it where each flat has got to.${line ? ` ${line}` : ''}`} action={canCreate ? <Button icon="Plus" disabled={!canSetUp} onClick={() => setCreating(true)}>Set up a sheet</Button> : undefined} />
        {creating && <CreateSheet propertyId={propertyId} propertyName={propertyName} onClose={() => setCreating(false)} onCreated={pick} />}
      </div>
    );
  }

  return (
    <div>
      {list.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">Sheet</span>
          <div className="flex max-w-full gap-1 overflow-x-auto pb-0.5">
            {list.map((s) => <SheetChip key={s.id} s={s} label={`${s.name} · ${s.status === 'LIVE' ? 'Open' : s.status === 'DRAFT' ? 'Setting up' : 'Closed'}`} on={s.id === sheetId} onClick={() => pick(s.id)} />)}
          </div>
        </div>
      )}
      {sheetId && <Sheet id={sheetId} onNew={canCreate ? () => setCreating(true) : undefined} canInvoice={canInvoice} onMenu={onMenu} />}
      {creating && <CreateSheet propertyId={propertyId} propertyName={propertyName} onClose={() => setCreating(false)} onCreated={pick} />}
    </div>
  );
}

function SheetChip({ s, label, on, onClick }: { s: SheetSummary; label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[12px] ${on ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.status === 'LIVE' ? 'bg-emerald-500' : s.status === 'DRAFT' ? 'bg-amber-500' : 'bg-ink-300'}`} />{label}
    </button>
  );
}

type View = 'grid' | 'table';
type StatKey = '' | 'available' | 'progress' | 'withstaff' | 'complete';

function Sheet({ id, onNew, canInvoice, onMenu }: { id: string; onNew?: () => void; canInvoice: boolean; onMenu?: (m: MenuEntry[]) => void }) {
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['/reservations/sheets', id], queryFn: () => reservationsApi.sheet(id) });
  // The parent passes a fresh onNew closure each render; read it through a ref so the menu effect below does not re-run (and re-render the page) forever.
  const onNewRef = useRef(onNew);
  useEffect(() => { onNewRef.current = onNew; }, [onNew]);
  const [search, setSearch] = useState('');
  const [floor, setFloor] = useState('');
  const [stage, setStage] = useState('');
  // Tiles are filters held in ?filter= (prototype §38), so a link can open the sheet already filtered.
  const tf = useStatFilter<Exclude<StatKey, ''>>();
  const stat: StatKey = tf.active ?? '';
  const [mineOnly, setMineOnly] = useState(false);
  const [view, setView] = useState<View>(() => { try { return (localStorage.getItem('lotus.res.view') as View) || 'table'; } catch { return 'table'; } });
  useEffect(() => { try { localStorage.setItem('lotus.res.view', view); } catch { /* private mode */ } }, [view]);
  const qc = useQueryClient();
  const canClose = useCan('reservations.close');
  const [closing, setClosing] = useState(false);
  const [editingFlats, setEditingFlats] = useState(false);
  const done = (d: SheetData) => { qc.setQueryData(['/reservations/sheets', id], d); void qc.invalidateQueries({ queryKey: ['/reservations/sheets'] }); };
  const m = useMutation({ mutationFn: async (fn: () => Promise<SheetData>) => fn(), onSuccess: (d) => { done(d); setClosing(false); } });
  const canManage = q.data?.can.manage ?? false;
  const status = q.data?.sheet.status;
  // One open sheet per development (as in the demo): on the development page "New sheet" appears only once this one is closed.
  const hasNew = !!onNew && status === 'CLOSED';
  // The ⋯ menu — the development page's own (through onMenu).
  const menu: MenuEntry[] = [
    canManage && status !== 'LIVE' && { label: 'Start the sheet', icon: 'Play', title: 'Agents assigned to the development see it from then on', onClick: () => m.mutate(() => reservationsApi.open(id)) },
    canManage && status === 'LIVE' && canClose && { label: 'Close the sheet', icon: 'Lock', danger: true, onClick: () => setClosing(true) },
    canManage && { label: 'Edit the flat list', icon: 'ListPlus', separator: true, onClick: () => setEditingFlats(true) },
    !!q.data && { label: 'Export CSV', icon: 'Download', onClick: () => { if (q.data) exportSheetCsv(q.data); } },
    hasNew && { label: 'New sheet', icon: 'Plus', separator: true, onClick: () => onNewRef.current?.() },
    canInvoice && !canManage && { label: 'My invoices', icon: 'Receipt', to: '/app/agent-bills' },
  ];
  // Hand the menu up only when something it shows has changed, or the page would re-render for ever.
  const menuRef = useRef(menu);
  useEffect(() => { menuRef.current = menu; });
  const menuKey = [status ?? '', canManage, canClose, hasNew, canInvoice, q.dataUpdatedAt].join('|');
  useEffect(() => {
    if (!onMenu || !q.data) return;
    onMenu(menuRef.current);
    return () => onMenu([]);
  }, [onMenu, menuKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Another agent's flat cannot be opened at all — it is "Reserved by …" and that is all; a deep link to it is dropped.
  const openable = (fid: string | null) => { const fl = fid ? q.data?.flats.find((x) => x.id === fid) : null; return !!fl && (fl.status === 'AVAILABLE' || fl.canSeeDetails); };
  const openFlat = openable(params.get('flat')) ? params.get('flat') : null;
  const setFlat = (fid: string | null) => { const n = new URLSearchParams(params); if (fid) n.set('flat', fid); else n.delete('flat'); setParams(n, { replace: true }); };

  const data = q.data;
  const flats = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    return data.flats.filter((f) => {
      if (floor && String(f.floor) !== floor) return false;
      if (stage && f.stage !== stage) return false;
      if (stat === 'progress' && ['available', 'withstaff', 'complete'].includes(f.stage)) return false;
      if (stat && stat !== 'progress' && f.stage !== stat) return false;
      if (mineOnly && !f.mine) return false;
      if (!term) return true;
      return [f.unitReference, f.unitName, agentLabel(f), f.applicant?.name, bedsLabel(f.bedrooms, f.unitType)].some((v) => String(v ?? '').toLowerCase().includes(term));
    });
  }, [data, search, floor, stage, stat, mineOnly]);

  if (q.isLoading || !data) return q.error ? <div className="py-12 text-center text-sm text-ink-400">{(q.error as Error).message}</div> : <PageLoader />;
  const { sheet, can } = data;
  const floors = [...new Set(data.flats.map((f) => f.floor))].sort((a, b) => (a ?? -1) - (b ?? -1));
  const mine = data.flats.filter((f) => f.mine).length;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[12.5px] text-ink-500">
        <i className={`h-2 w-2 rounded-full ${sheet.status === 'LIVE' ? 'bg-emerald-500' : sheet.status === 'DRAFT' ? 'bg-amber-500' : 'bg-ink-300'}`} />
        <span className="font-semibold text-ink-800">{sheet.name}</span>
        <span>· {sheet.status === 'LIVE' ? 'Open to agents' : sheet.status === 'DRAFT' ? 'Being set up' : 'Closed'}</span>
      </div>

      <FormError error={m.error} />
      {editingFlats && can.manage && <FlatListModal data={data} onClose={() => setEditingFlats(false)} onChanged={done} />}
      <Confirm open={closing} onClose={() => setClosing(false)} title="Close this reservation sheet?" confirmLabel="Close it"
        body="Agents lose access to it immediately — only Lotus Living staff will be able to open it. Nothing on the sheet is lost, and it can be started again." pending={m.isPending} error={m.error}
        onConfirm={() => m.mutate(() => reservationsApi.close(id))} />

      <div className="mb-2.5 grid grid-cols-2 gap-2.5 sm:grid-cols-5">
        <Tile label="Flats" value={sheet.counts.flats} on={stat === ''} onClick={tf.clear} />
        <Tile label="Available" value={sheet.counts.available} on={stat === 'available'} onClick={() => tf.toggle('available')} />
        <Tile label="In progress" value={sheet.counts.inProgress} tone="#d4932c" on={stat === 'progress'} onClick={() => tf.toggle('progress')} />
        <Tile label="With Lotus Living" value={sheet.counts.withStaff} tone="#2f9e6e" on={stat === 'withstaff'} onClick={() => tf.toggle('withstaff')} />
        <Tile label="Moved in" value={sheet.counts.movedIn} tone="#2f9e6e" on={stat === 'complete'} onClick={() => tf.toggle('complete')} />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Flat, tenant or agent…" className="!w-full !py-1.5 !text-[12.5px] sm:!w-56" />
        <FilterMenu value={{ floor: floor ? [floor] : [], stage: stage ? [stage] : [], mine: mineOnly ? ['mine'] : [] }}
          onChange={(v) => { setFloor(v.floor?.[0] ?? ''); setStage(v.stage?.[0] ?? ''); setMineOnly((v.mine ?? []).includes('mine')); }}
          groups={[
            { key: 'floor', label: 'Floor', single: true, options: floors.map((f) => ({ value: String(f), label: floorLabel(f) })) },
            { key: 'stage', label: 'Stage', single: true, options: sheet.stages.map((s) => ({ value: s.key, label: s.label })) },
            ...(!can.manage && mine > 0 ? [{ key: 'mine', label: 'Mine', single: true, options: [{ value: 'mine', label: `Only my flats (${mine})` }] }] : []),
          ]} />
        <Segmented className="ml-auto" value={view} onChange={setView} items={[{ key: 'grid' as const, label: 'Cards' }, { key: 'table' as const, label: 'Table' }]} />
      </div>


      {!flats.length ? <div className="surface px-4 py-10 text-center text-[13px] text-ink-400">No flats match those filters.</div>
        : view === 'table' ? <FlatTable flats={flats} onOpen={setFlat} />
          : floors.filter((fl) => flats.some((f) => f.floor === fl)).map((fl) => {
            const items = flats.filter((f) => f.floor === fl);
            return (
              <section key={String(fl)} className="mb-5">
                <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">{floorLabel(fl)}<span className="rounded-full bg-ink-100 px-1.5 text-ink-500">{items.length}</span><span className="h-px flex-1 bg-ink-100" /></div>
                <div className="grid grid-cols-1 gap-2.5 min-[430px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                  {items.map((f) => <FlatCard key={f.id} f={f} onOpen={() => setFlat(f.id)} />)}
                </div>
              </section>
            );
          })}

      {openFlat && <OpenFlat id={openFlat} flats={data.flats} onClose={() => setFlat(null)} />}
    </>
  );
}

function Tile({ label, value, tone, on, onClick }: { label: string; value: number; tone?: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={`surface px-3.5 py-3 text-left transition ${on ? 'border-bronze-500 bg-bronze-50 ring-1 ring-bronze-500' : 'hover:border-ink-300'}`}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-1 text-[22px] font-bold leading-tight tabular-nums" style={{ color: tone ?? 'var(--color-ink-900)' }}>{value}</div>
    </button>
  );
}


function FlatCard({ f, onOpen }: { f: Flat; onOpen: () => void }) {
  const booked = f.status !== 'AVAILABLE';
  const moved = f.stage === 'complete';
  const rent = f.rentMinor ?? f.listRentMinor;
  const locked = booked && !f.canSeeDetails;   // another agent's tenant: nothing to open
  return (
    <button type="button" onClick={locked ? undefined : onOpen} disabled={locked} title={locked ? f.masked ?? undefined : undefined} className={`surface relative flex overflow-hidden text-left transition ${locked ? 'cursor-default opacity-70' : 'hover:shadow-md'} ${booked ? '' : 'opacity-[0.92]'}`} style={{ borderLeft: `4px solid ${f.stageColour}` }}>
      <div className="min-w-0 flex-1 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <div className="font-mono text-[15px] font-semibold text-ink-900">{f.unitReference}</div>
          <div className="min-w-0 flex-1 text-[11px] leading-tight text-ink-500">{bedsLabel(f.bedrooms, f.unitType)}<br />{f.areaSqft ? `${f.areaSqft} sq ft` : ' '}</div>
          <div className="text-right">
            <div className="text-[13px] font-semibold tabular-nums text-ink-800">{formatMoney(rent, f.currency).replace(/\.00$/, '')}</div>
            {f.rentMinor !== null && f.rentMinor !== f.listRentMinor ? <div className="text-[10px] tabular-nums text-ink-400 line-through">{formatMoney(f.listRentMinor, f.currency).replace(/\.00$/, '')}</div> : <div className="text-[10px] text-ink-400">pcm</div>}
          </div>
        </div>
        {!moved && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{booked ? <RoadmapChip r={f} /> : <PhaseChip f={f} />}</div>}
        {booked && (
          <div className="mt-1.5 truncate text-[11.5px] text-ink-600">
            {f.canSeeDetails ? <><b className="font-medium text-ink-800">{f.applicant?.name ?? 'Reserved'}</b>{agentLabel(f) ? ` · ${agentLabel(f)}` : ''}</>
              : <span className="inline-flex items-center gap-1 text-ink-400"><Lock className="h-3 w-3" />{f.masked}</span>}
          </div>
        )}
        {(f.parking || f.furniture) && (
          <div className="mt-1 flex gap-1">{f.parking && <span className="rounded bg-ink-100 px-1.5 text-[10px] text-ink-600">Parking ×{f.parkingSpaces ?? 1}</span>}{f.furniture && <span className="rounded bg-ink-100 px-1.5 text-[10px] text-ink-600">Furnished</span>}</div>
        )}
        <div className="mt-2">{moved ? <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-emerald-700"><Check className="h-3.5 w-3.5" />Moved in</span> : <RoadmapDots roadmap={f.roadmap} />}</div>
      </div>
      <div className="grid w-6 shrink-0 place-items-center text-[9.5px] font-bold text-on-primary [writing-mode:vertical-rl]" style={{ background: f.agent?.colour ?? (f.direct ? DIRECT_COLOUR : 'var(--color-ink-100)') }} title={agentLabel(f) ?? undefined}>{f.agent ? initials(f.agent.name) : f.direct ? 'APP' : ''}</div>
    </button>
  );
}

function FlatTable({ flats, onOpen }: { flats: Flat[]; onOpen: (id: string) => void }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="w-full min-w-[720px] text-[12.5px]">
        <thead><tr className="bg-ink-50/60 text-left text-[11.5px] font-medium uppercase tracking-wide text-ink-500">
          <th className="px-3 py-1.5">Flat</th><th className="px-3 py-1.5">Floor</th><th className="px-3 py-1.5">Type</th><th className="px-3 py-1.5">Size</th><th className="px-3 py-1.5 text-right">Rent pcm</th>
          <th className="px-3 py-1.5">Where it is</th><th className="px-3 py-1.5">Agent</th><th className="px-3 py-1.5">Tenant</th><th className="px-3 py-1.5">Steps</th>
        </tr></thead>
        <tbody>{flats.map((f) => (
          <tr key={f.id} onClick={() => { if (f.status === 'AVAILABLE' || f.canSeeDetails) onOpen(f.id); }} className={`border-b border-ink-100 ${f.status !== 'AVAILABLE' && !f.canSeeDetails ? 'opacity-70' : 'cursor-pointer hover:bg-ink-50/60'}`} style={{ boxShadow: f.agent ? `inset 3px 0 0 ${f.agent.colour}` : undefined }}>
            <td className="px-3 py-1.5 font-mono font-semibold">{f.unitReference}</td>
            <td className="px-3 py-1.5">{floorLabel(f.floor)}</td>
            <td className="px-3 py-1.5">{bedsLabel(f.bedrooms, f.unitType)}</td>
            <td className="px-3 py-1.5">{f.areaSqft ? `${f.areaSqft} sq ft` : '—'}</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{formatMoney(f.rentMinor ?? f.listRentMinor, f.currency)}</td>
            <td className="px-3 py-1.5">{f.roadmap ? <RoadmapChip r={f} /> : <PhaseChip f={f} />}</td>
            <td className="px-3 py-1.5 text-ink-600">{agentLabel(f) ?? '—'}</td>
            <td className="px-3 py-1.5">{f.status === 'AVAILABLE' ? <span className="text-ink-300">—</span> : f.canSeeDetails ? f.applicant?.name : <span className="inline-flex items-center gap-1 text-ink-400"><Lock className="h-3 w-3" />{f.masked}</span>}</td>
            <td className="px-3 py-1.5">{f.status === 'AVAILABLE' ? <span className="text-ink-300">—</span> : <RoadmapDots roadmap={f.roadmap} />}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

// ─── One flat ────────────────────────────────────────────────────────────────
// A booked flat (anything but AVAILABLE) opens the ApplicationDrawer; a free one opens the ReferDrawer (Register for home —
// the same drawer as Lettings → Available; it needs the flat's published advert).

function OpenFlat({ id, flats, onClose }: { id: string; flats: Flat[]; onClose: () => void }) {
  const f = flats.find((x) => x.id === id);
  if (f && f.status !== 'AVAILABLE') return <ApplicationDrawer id={id} onClose={onClose} />;
  return f ? <ReferDrawer unitId={f.unitId} onClose={onClose} /> : null;
}

function CreateSheet({ propertyId: fixedId, propertyName, onClose, onCreated }: { propertyId?: string; propertyName?: string; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  // From a development's Sheets tab the development is fixed, so the list is not fetched.
  const props = useQuery({ queryKey: ['/properties', 'options'], queryFn: async () => (await api.get<Array<{ id: string; name: string }>>('/properties', { perPage: 100 })).data, enabled: !fixedId });
  const [propertyId, setPropertyId] = useState(fixedId ?? '');
  const [name, setName] = useState('');
  const create = useMutation({
    // A sheet starts open to the agents assigned to the development; Close the sheet in the ⋯ menu takes it away again.
    mutationFn: () => reservationsApi.createSheet({ propertyId, name: name.trim() || undefined, open: true }),
    onSuccess: (d) => { void qc.invalidateQueries({ queryKey: ['/reservations/sheets'] }); onCreated(d.sheet.id); onClose(); },
  });
  return (
    <Modal open onClose={onClose} title="Set up a reservation sheet"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!propertyId || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Setting up…' : 'Set it up'}</Button></>}>
      <div className="space-y-4">
        <Field label="Development" hint="Every flat that is not occupied and has no tenancy lined up goes on the sheet; you can take flats off later.">
          {fixedId
            ? <div className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-[13px] text-ink-800">{propertyName ?? 'This development'}</div>
            : <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-full"><option value="">Choose…</option>{props.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>}
        </Field>
        <Field label="Name (optional)"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Eaton Court — launch" className="w-full" /></Field>
        <p className="text-[12px] text-ink-500">The sheet starts straight away: agents assigned to the development see it. Close it from the ⋯ menu when the launch is over.</p>
        <FormError error={create.error} />
      </div>
    </Modal>
  );
}

/** The sheet as the person sees it (another agent's applicants stay masked), one row per flat. */
function exportSheetCsv(d: SheetData) {
  const esc = (v: unknown) => { const t = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const money = (n: number | null) => (n === null ? '' : (n / 100).toFixed(2));
  const rows = [
    ['Flat', 'Floor', 'Type', 'Size (sq ft)', 'List rent pcm', 'Agreed rent pcm', 'Stage', 'Agent', 'Reserved for', 'Move-in', 'Steps done', 'Documents', 'Status'],
    ...d.flats.map((f) => [f.unitReference, floorLabel(f.floor), bedsLabel(f.bedrooms, f.unitType), f.areaSqft ?? '', money(f.listRentMinor), money(f.rentMinor), f.stageLabel,
      agentLabel(f) ?? '', f.applicant?.name ?? f.masked ?? '', f.startDate ?? '', `${f.stepsDone}/${f.stepsTotal}`, `${f.docs.uploaded}/${f.docs.required}`, f.status]),
  ];
  const blob = new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${d.sheet.propertyName.replace(/[^\w]+/g, '-').toLowerCase()}-reservation-sheet.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── The flat list (prototype §18 "Edit the flat list" / "Paste from a spreadsheet") ─────────────────────────────────

const FLOOR_WORDS: Record<string, number> = { lg: -1, 'lower ground': -1, basement: -1, b: -1, g: 0, gf: 0, ground: 0, 'ground floor': 0, first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10 };
const parseFloor = (v: string): number | null => {
  const s = v.trim().toLowerCase().replace(/\s*floor$/, '');
  if (!s) return null;
  if (s in FLOOR_WORDS) return FLOOR_WORDS[s]!;
  const m = s.match(/^-?\d+/);
  return m ? Number(m[0]) : null;
};
const parseType = (v: string): { unitType: string; bedrooms: number } => {
  const s = v.trim().toLowerCase();
  if (!s || s.startsWith('studio')) return { unitType: 'STUDIO', bedrooms: 0 };
  const beds = s.match(/(\d+)\s*(bed|br|b\b)/);
  const type = s.includes('duplex') ? 'DUPLEX' : s.includes('town') ? 'TOWNHOUSE' : s.includes('villa') ? 'VILLA' : 'APARTMENT';
  return { unitType: type, bedrooms: beds ? Number(beds[1]) : 1 };
};
const parseNumber = (v: string): number | null => { const n = Number(v.replace(/[£$€,\s]|sq\s*ft|sqft|pcm/gi, '')); return Number.isFinite(n) && v.trim() ? n : null; };

/** One pasted line → a row. Columns in the demo's order: Floor, Flat no., Type, Size, Rent; tab, comma or two-plus spaces between them. */
function parsePastedFlats(text: string): PastedFlat[] {
  const out: PastedFlat[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const cols = line.includes('\t') ? line.split('\t') : line.includes(',') ? line.split(',') : line.split(/\s{2,}/);
    const c = cols.map((x) => x.trim());
    if (/^floor$/i.test(c[0] ?? '') && /flat/i.test(c[1] ?? '')) continue;   // a pasted header row
    // A single column is just a list of flat numbers.
    if (c.length === 1) { out.push({ reference: c[0]! }); continue; }
    const floor = parseFloor(c[0] ?? '');
    const reference = c[1] ?? '';
    if (!reference) continue;
    const { unitType, bedrooms } = parseType(c[2] ?? '');
    const size = parseNumber(c[3] ?? '');
    const rent = parseNumber(c[4] ?? '');
    out.push({ reference, floor, unitType, bedrooms, areaSqft: size === null ? null : Math.round(size), rentMinor: rent === null ? null : Math.round(rent * 100) });
  }
  return out;
}

function FlatListModal({ data, onClose, onChanged }: { data: SheetData; onClose: () => void; onChanged: (d: SheetData) => void }) {
  const qc = useQueryClient();
  const { sheet } = data;
  const canAddUnits = useCan('units.create');
  const [mode, setMode] = useState<'list' | 'add' | 'paste'>('list');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [result, setResult] = useState<AddFlatsResult | null>(null);
  const [removing, setRemoving] = useState<Flat | null>(null);
  const eligible = useQuery({ queryKey: ['/reservations/eligible', sheet.propertyId], queryFn: () => reservationsApi.eligible(sheet.propertyId) });
  const refreshAll = () => { void qc.invalidateQueries({ queryKey: ['/reservations/eligible', sheet.propertyId] }); void qc.invalidateQueries({ queryKey: ['flats', sheet.propertyId] }); void qc.invalidateQueries({ queryKey: ['/units'] }); };
  const m = useMutation({
    mutationFn: async (fn: () => Promise<SheetData>) => fn(),
    onSuccess: (d) => { onChanged(d); refreshAll(); setPicked(new Set()); setRemoving(null); setMode('list'); },
  });
  const paste = useMutation({
    mutationFn: (flats: PastedFlat[]) => reservationsApi.addFlats(sheet.id, flats),
    onSuccess: ({ sheet: d, result: r }) => { onChanged(d); refreshAll(); setResult(r); setText(''); },
  });
  const rows = parsePastedFlats(text);
  const flats = [...data.flats].sort((a, b) => (a.floor ?? -99) - (b.floor ?? -99) || a.unitReference.localeCompare(b.unitReference, undefined, { numeric: true }));
  const free = eligible.data?.flats ?? [];

  return (
    <Modal open onClose={onClose} size="lg" title="The flat list"
      footer={<>
        <Button variant="secondary" onClick={onClose}>Close</Button>
        {mode === 'add' && <Button disabled={!picked.size || m.isPending} onClick={() => m.mutate(() => reservationsApi.updateSheet(sheet.id, { addUnitIds: [...picked] }))}>{m.isPending ? 'Adding…' : `Add ${picked.size || ''} to the sheet`}</Button>}
        {mode === 'paste' && <Button disabled={!rows.length || paste.isPending} onClick={() => paste.mutate(rows)}>{paste.isPending ? 'Adding…' : `Add ${rows.length || ''} flat${rows.length === 1 ? '' : 's'}`}</Button>}
      </>}>
      <div className="space-y-4">
        <p className="text-[12.5px] text-ink-500">One row per flat on {sheet.propertyName}'s sheet. Agents cannot change any of this. A flat can be taken off only while nobody has reserved it.</p>
        <div className="flex flex-wrap gap-2">
          {([['list', `On the sheet (${data.flats.length})`], ['add', `Add a flat${free.length ? ` (${free.length} free)` : ''}`], ['paste', 'Paste from a spreadsheet']] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setMode(k); setResult(null); }} className={`h-7 rounded-lg border px-2.5 text-[12px] ${mode === k ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'}`}>{l}</button>
          ))}
        </div>

        {mode === 'list' && (
          <div className="max-h-[52vh] overflow-auto rounded-xl border border-ink-100">
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 bg-ink-50/60 text-[11.5px] font-medium uppercase tracking-wide text-ink-500"><tr><th className="px-3 py-1.5 text-left">Flat</th><th className="px-3 py-1.5 text-left">Floor</th><th className="px-3 py-1.5 text-left">Type</th><th className="px-3 py-1.5 text-right">Size</th><th className="px-3 py-1.5 text-right">Rent</th><th className="px-3 py-1.5 text-left">Stage</th><th className="px-3 py-1.5" /></tr></thead>
              <tbody>
                {flats.map((f) => (
                  <tr key={f.id} className="border-t border-ink-100">
                    <td className="px-3 py-1.5 font-mono font-semibold text-ink-800">{f.unitReference}</td>
                    <td className="px-3 py-1.5 text-ink-600">{floorLabel(f.floor)}</td>
                    <td className="px-3 py-1.5 text-ink-600">{bedsLabel(f.bedrooms, f.unitType)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-ink-600">{f.areaSqft ? `${f.areaSqft} sq ft` : '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-ink-600">{formatMoney(f.listRentMinor, f.currency)}</td>
                    <td className="px-3 py-1.5">{f.roadmap ? <RoadmapChip r={f} /> : <StageChip f={f} />}</td>
                    <td className="px-3 py-1.5 text-right">{f.status === 'AVAILABLE' && <button type="button" title="Take off the sheet" onClick={() => setRemoving(f)} className="text-ink-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}</td>
                  </tr>
                ))}
                {!flats.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-ink-400">No flats on the sheet yet — add one, or paste a list.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {mode === 'add' && (
          eligible.isLoading ? <SkeletonRows rows={4} cols={3} />
          : !free.length ? <div className="surface px-4 py-6 text-center text-[12.5px] text-ink-500">Every free flat of {sheet.propertyName} is on the sheet already. A flat that is let, or has a tenancy lined up, cannot go on. To add a flat that does not exist yet, paste it from a spreadsheet.</div>
          : <div className="max-h-[52vh] overflow-auto rounded-xl border border-ink-100">
              {free.map((u) => (
                <label key={u.unitId} className="flex cursor-pointer items-center gap-3 border-t border-ink-100 px-3 py-2 text-[12.5px] first:border-t-0 hover:bg-ink-50">
                  <input type="checkbox" checked={picked.has(u.unitId)} onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(u.unitId); else n.delete(u.unitId); return n; })} />
                  <span className="w-20 font-mono font-semibold text-ink-800">{u.reference}</span>
                  <span className="w-28 text-ink-600">{floorLabel(u.floor)}</span>
                  <span className="w-16 text-ink-600">{bedsLabel(u.bedrooms, u.unitType)}</span>
                  <span className="w-20 text-right tabular-nums text-ink-600">{u.areaSqft ? `${u.areaSqft} sq ft` : '—'}</span>
                  <span className="w-24 text-right tabular-nums text-ink-600">{formatMoney(u.listRentMinor, u.currency)}</span>
                  <span className="ml-auto text-[11px] text-ink-400">{u.status.toLowerCase().replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
        )}

        {mode === 'paste' && (
          <div className="space-y-3">
            <p className="text-[12.5px] text-ink-500">Copy the columns <b>Floor, Flat no., Type, Size, Rent</b> straight out of Excel and paste below, one flat per line. A flat number the development already has is put on the sheet if it is free; a new one is created as a unit{canAddUnits ? '' : ' — which your role cannot do, so only existing flats will be added'}.</p>
            <Textarea rows={6} value={text} onChange={(e) => { setText(e.target.value); setResult(null); }} placeholder={'Ground\t1\t1 bed\t419.8\t1250\nGround\t2\t2 bed\t592\t1500'} className="w-full font-mono text-[12px]" />
            {rows.length > 0 && (
              <div className="max-h-[32vh] overflow-auto rounded-xl border border-ink-100">
                <table className="w-full text-[12.5px]">
                  <thead className="sticky top-0 bg-ink-50/60 text-[11.5px] font-medium uppercase tracking-wide text-ink-500"><tr><th className="px-3 py-1.5 text-left">Flat</th><th className="px-3 py-1.5 text-left">Floor</th><th className="px-3 py-1.5 text-left">Type</th><th className="px-3 py-1.5 text-right">Size</th><th className="px-3 py-1.5 text-right">Rent pcm</th></tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-ink-100">
                        <td className="px-3 py-1.5 font-mono font-semibold text-ink-800">{r.reference}</td>
                        <td className="px-3 py-1.5 text-ink-600">{r.floor === undefined ? '—' : floorLabel(r.floor)}</td>
                        <td className="px-3 py-1.5 text-ink-600">{r.unitType ? bedsLabel(r.bedrooms ?? 0, r.unitType) : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-ink-600">{r.areaSqft ? `${r.areaSqft} sq ft` : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-ink-600">{r.rentMinor ? formatMoney(r.rentMinor, sheet.region === 'AE' ? 'AED' : 'GBP') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {result && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-900">
                <b>{result.added} added to the sheet</b>{result.created ? `, ${result.created} created as new flats` : ''}.
                {result.skipped.length > 0 && <ul className="mt-1 list-disc pl-5 text-amber-900">{result.skipped.map((s, i) => <li key={i}><span className="font-mono">{s.reference}</span>: {s.why}</li>)}</ul>}
              </div>
            )}
            <FormError error={paste.error} />
          </div>
        )}
        <FormError error={m.error} />
      </div>
      <Confirm open={!!removing} onClose={() => setRemoving(null)} title={`Take ${removing?.unitReference ?? ''} off the sheet?`} confirmLabel="Take it off"
        body="Agents will no longer see it on the sheet. The flat itself is not changed, and it can be put back while it is free." pending={m.isPending} error={m.error}
        onConfirm={() => removing && m.mutate(() => reservationsApi.updateSheet(sheet.id, { removeUnitIds: [removing.unitId] }))} />
    </Modal>
  );
}
